import { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { useAppContext } from '../contexts/AppContext.tsx';
import { TeamPerformanceData } from '../contexts/AppContext.tsx';
import { TeamPerformanceRefreshService } from '../services/teamPerformanceRefreshService.ts';
import { logger } from '../utils/logger.ts';

interface UseTeamPerformanceDataResult {
  performanceData: TeamPerformanceData[];
  totalActualSales: number;
  totalWorkingDays: number;  // 🆕 전체 근무일 (합집합)
  isLoading: boolean;
  isCalculating: boolean;
  lastCalculatedAt: string;
  dataSource: 'cached' | 'calculated' | 'none';
  refreshData: () => Promise<void>;
  error: string | null;
}

export const useTeamPerformanceData = (currentDate: Date): UseTeamPerformanceDataResult => {
  const {
    teams,
    employees,
    budgetData,
    setBudgetData,
    isLoading: contextLoading,
    dataLoadError,
    triggerActualSalesRefreshAfterExcelUpload
  } = useAppContext();
  
  const [performanceData, setPerformanceData] = useState<TeamPerformanceData[]>([]);
  const [totalActualSales, setTotalActualSales] = useState<number>(0);
  const [totalWorkingDays, setTotalWorkingDays] = useState<number>(0);  // 🆕 전체 근무일
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [lastCalculatedAt, setLastCalculatedAt] = useState<string>('');
  const [dataSource, setDataSource] = useState<'cached' | 'calculated' | 'none'>('none');
  const [error, setError] = useState<string | null>(null);
  
  // 년월 문자열 변환
  const yearMonth = format(currentDate, 'yyyy-MM');
  
  // 🔧 월별 계산 상태 분리: 각 월의 계산 상태를 독립적으로 관리
  const calculatingMonthsRef = useRef<Set<string>>(new Set());
  const isCurrentMonthCalculating = calculatingMonthsRef.current.has(yearMonth);
  
  // 이전 계산 완료 추적을 위한 ref (하위 호환성 유지)
  const isCalculatingRef = useRef(false);

  // 🔧 FIX: budgetData/setBudgetData/triggerRefresh를 ref로 추적하여 useEffect deps에서 제거 (무한 루프 방지)
  const budgetDataRef = useRef(budgetData);
  budgetDataRef.current = budgetData;

  const setBudgetDataRef = useRef(setBudgetData);
  setBudgetDataRef.current = setBudgetData;

  const triggerRefreshRef = useRef(triggerActualSalesRefreshAfterExcelUpload);
  triggerRefreshRef.current = triggerActualSalesRefreshAfterExcelUpload;

  // 🔧 FIX C14: 재귀 재시도 제한 상수 및 refs
  const MAX_RETRY_COUNT = 15; // 15회 × 200ms = 최대 3초 대기
  const retryCountRef = useRef<number>(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 🔧 자동 새로고침 실행 상태 추적
  const autoRefreshExecutedRef = useRef<Set<string>>(new Set());

  // 데이터 새로고침 (강제 재계산) - 새로운 재사용 서비스 활용
  const refreshData = useCallback(async () => {
    if (contextLoading) {
      logger.debug('[useTeamPerformanceData] refreshData: 컨텍스트 로딩 중 - 대기');
      return;
    }

    // 🔧 월별 계산 상태 확인: 현재 월이 계산 중인지 체크
    if (calculatingMonthsRef.current.has(yearMonth)) {
      if (retryCountRef.current >= MAX_RETRY_COUNT) {
        logger.debug(`[useTeamPerformanceData] refreshData: ${yearMonth} 최대 재시도 횟수(${MAX_RETRY_COUNT}) 초과 - 중단`);
        retryCountRef.current = 0;
        setError(`${yearMonth} 데이터 계산 시간 초과. 새로고침을 다시 시도해주세요.`);
        return;
      }
      retryCountRef.current += 1;
      logger.debug(`[useTeamPerformanceData] refreshData: ${yearMonth} 계산 중 - 200ms 후 재시도 (${retryCountRef.current}/${MAX_RETRY_COUNT})`);
      retryTimeoutRef.current = setTimeout(() => {
        if (!calculatingMonthsRef.current.has(yearMonth)) {
          refreshData();
        }
      }, 200);
      return;
    }

    // 🔧 현재 월 계산 상태 설정
    calculatingMonthsRef.current.add(yearMonth);
    retryCountRef.current = 0; // 🔧 FIX C14: 계산 진입 성공 시 재시도 카운트 리셋
    isCalculatingRef.current = true;
    setIsCalculating(true);
    setError(null);
    
    try {
      // 🔧 FIX: triggerRefresh 이중 호출 제거 — RefreshService가 내부적으로 ActualSalesService 호출
      logger.debug(`[useTeamPerformanceData] ${yearMonth} 강제 새로고침 시작 (단일 경로)`);

      // ✅ 월별 저장 직원 데이터 우선 사용 (급여 관리와 동일)
      // 🔧 FIX: budgetDataRef.current 사용 (deps에서 제거했으므로)
      const currentBudgetData = budgetDataRef.current;
      const monthlyEmployees = currentBudgetData.monthlyData?.[yearMonth]?.employees;
      const targetEmployees = (monthlyEmployees && monthlyEmployees.length > 0)
        ? monthlyEmployees
        : employees;

      if (targetEmployees !== employees) {
        logger.debug(`[useTeamPerformanceData] ${yearMonth} 월별 저장 직원 데이터 사용`, { context: { count: targetEmployees.length } });
      } else {
        logger.debug(`[useTeamPerformanceData] ${yearMonth} 전역 직원 데이터 사용`, { context: { count: targetEmployees.length } });
      }

      // 팀 성과 데이터 새로고침 실행
      const result = await TeamPerformanceRefreshService.refreshTeamPerformanceData(
        currentDate,
        teams,
        targetEmployees,
        currentBudgetData,
        setBudgetDataRef.current,
        {
          forceRefresh: true,
          saveData: true,
          showLogs: true
        }
      );

      if (result.success) {
        setPerformanceData(result.performanceData);
        setTotalActualSales(result.totalActualSales);
        setTotalWorkingDays(result.totalWorkingDays);  // 🆕 전체 근무일
        setLastCalculatedAt(result.lastCalculatedAt);
        setDataSource(result.dataSource);
        logger.debug(`[useTeamPerformanceData] ${yearMonth} 강제 새로고침 완료`);
      } else {
        throw new Error(result.error || '새로고침에 실패했습니다');
      }

    } catch (err) {
      logger.error('[useTeamPerformanceData] 새로고침 실패', err instanceof Error ? err : undefined);
      setError(err instanceof Error ? err.message : '새로고침 실패');
    } finally {
      // 🔧 현재 월 계산 상태 정리
      calculatingMonthsRef.current.delete(yearMonth);
      setIsLoading(false); // 🔧 중요: 로딩 상태도 완료로 설정
      setIsCalculating(false);
      isCalculatingRef.current = false;
    }
  }, [currentDate, yearMonth, teams, employees, contextLoading]); // 🔧 FIX: budgetData, setBudgetData, triggerRefresh 제거 (ref로 추적)

  // 데이터 로드 및 계산 - 새로운 재사용 서비스 활용
  useEffect(() => {
    const loadData = async () => {
      // 🔧 기본 조건 확인: 컨텍스트 로딩 및 현재 월 계산 상태 체크
      if (contextLoading) {
        logger.debug(`[useTeamPerformanceData] ${yearMonth} 컨텍스트 로딩 중 - 대기`);
        // 🔧 컨텍스트 로딩 중일 때는 로딩 상태 유지
        setIsLoading(true);
        setIsCalculating(false);
        return;
      }

      // 🔧 FIX: AppContext 데이터 로드 오류 시 실제 Firestore 오류 메시지 표시
      if (dataLoadError) {
        logger.warn(`[useTeamPerformanceData] ${yearMonth} AppContext 데이터 로드 오류`, { context: { error: dataLoadError } });
        setError(`Firestore 데이터 로드 실패: ${dataLoadError}`);
        setIsLoading(false);
        setIsCalculating(false);
        return;
      }

      // 🔧 현재 월이 계산 중인지 확인 (다른 월 계산은 영향 없음)
      if (calculatingMonthsRef.current.has(yearMonth)) {
        logger.debug(`[useTeamPerformanceData] ${yearMonth} 계산 중 - 대기`);
        // 🔧 계산 중일 때는 로딩 상태 유지 (이전 데이터 숨김)
        setIsLoading(true);
        setIsCalculating(true);
        return;
      }

      // 🔧 현재 월 계산 상태 설정
      calculatingMonthsRef.current.add(yearMonth);
      isCalculatingRef.current = true;
      setIsLoading(true);
      setIsCalculating(true);
      setError(null);
      
      try {
        // 🔧 FIX: triggerRefresh 이중 호출 제거 — RefreshService에 위임
        logger.debug(`[useTeamPerformanceData] ${yearMonth} 데이터 로드 시작 (캐시 우선)`);

        // ✅ 월별 저장 직원 데이터 우선 사용 (급여 관리와 동일)
        // 🔧 FIX: budgetDataRef.current 사용 (deps에서 제거했으므로)
        const currentBudgetData = budgetDataRef.current;
        const monthlyEmployees = currentBudgetData.monthlyData?.[yearMonth]?.employees;
        const targetEmployees = (monthlyEmployees && monthlyEmployees.length > 0)
          ? monthlyEmployees
          : employees;

        if (targetEmployees !== employees) {
          logger.debug(`[useTeamPerformanceData] ${yearMonth} 월별 저장 직원 데이터 사용`, { context: { count: targetEmployees.length } });
        } else {
          logger.debug(`[useTeamPerformanceData] ${yearMonth} 전역 직원 데이터 사용`, { context: { count: targetEmployees.length } });
        }

        const result = await TeamPerformanceRefreshService.refreshTeamPerformanceData(
          currentDate,
          teams,
          targetEmployees,
          currentBudgetData,
          setBudgetDataRef.current,
          {
            forceRefresh: true, // 🔧 FIX: 항상 재계산 — 캐시에 totalWorkingDays 누락 문제 방지
            saveData: true,
            showLogs: true
          }
        );

        if (result.success) {
          setPerformanceData(result.performanceData);
          setTotalActualSales(result.totalActualSales);
          setTotalWorkingDays(result.totalWorkingDays);  // 🆕 전체 근무일
          setLastCalculatedAt(result.lastCalculatedAt);
          setDataSource(result.dataSource);
          logger.debug(`[useTeamPerformanceData] ${yearMonth} 데이터 로드 완료`);
        } else {
          // 실패 시 안전한 기본값 설정
          logger.warn(`[useTeamPerformanceData] ${yearMonth} 데이터 로드 실패`, { context: { error: result.error } });
          setError(result.error || '데이터 로드 실패');

          // 팀 데이터를 기반으로 안전한 기본값 생성 - null/undefined 에러 방지
          const safeTeams = Array.isArray(teams) ? teams : [];
          const defaultData = safeTeams
            .filter(team => team && typeof team === 'object' && team.name) // 유효한 팀만 필터링
            .map(team => {
              try {
                return {
                  teamName: team?.name || '알 수 없는 팀',
                  targetSales: 0,
                  actualSales: 0,
                  achievementRate: 0,
                  incentiveAmount: 0,
                  exceededSales: 0,
                  underperformanceSales: 0,
                  deductionAmount: 0,
                  incentiveCalculationSales: 0
                };
              } catch (error) {
                logger.error('[useTeamPerformanceData] 기본 데이터 생성 에러', error instanceof Error ? error : undefined);
                return {
                  teamName: '에러 팀',
                  targetSales: 0,
                  actualSales: 0,
                  achievementRate: 0,
                  incentiveAmount: 0,
                  exceededSales: 0,
                  underperformanceSales: 0,
                  deductionAmount: 0,
                  incentiveCalculationSales: 0
                };
              }
            });
          setPerformanceData(defaultData);
          setTotalActualSales(0);
          setDataSource('none');
        }

      } catch (err) {
        logger.error('[useTeamPerformanceData] 데이터 로드 예외', err instanceof Error ? err : undefined);
        setError(err instanceof Error ? err.message : '데이터 로드 실패');
        
        // 예외 발생 시도 안전한 기본값 설정
        setPerformanceData([]);
        setTotalActualSales(0);
        setDataSource('none');
      } finally {
        // 🔧 현재 월 계산 상태 정리
        calculatingMonthsRef.current.delete(yearMonth);
        setIsLoading(false);
        setIsCalculating(false);
        isCalculatingRef.current = false;
      }
    };

    loadData();
  }, [currentDate, yearMonth, teams, employees, contextLoading, dataLoadError]); // 🔧 FIX: budgetData, setBudgetData, triggerRefresh 제거 (ref로 추적 - 무한 루프 방지)

  // 🔧 월 변경 시 상태 초기화: 이전 월 데이터 제거 및 새 월 준비
  useEffect(() => {
    logger.debug(`[useTeamPerformanceData] ${yearMonth} 월 변경 감지 - 상태 초기화 시작`);

    // 🔧 이전 월 데이터 즉시 초기화
    setPerformanceData([]);
    setTotalActualSales(0);
    setTotalWorkingDays(0);  // 🆕 전체 근무일 초기화
    setDataSource('none');
    setLastCalculatedAt('');
    setError(null);
    setIsLoading(true); // 🔧 중요: 새 월 데이터 로딩 시작을 위해 로딩 상태 설정
    setIsCalculating(false);

    // 🔧 자동 새로고침 실행 상태도 초기화 (이전 월 기록 삭제)
    autoRefreshExecutedRef.current.clear();

    logger.debug(`[useTeamPerformanceData] ${yearMonth} 상태 초기화 완료 - 새 월 데이터 준비됨`);

    return () => {
      // 컴포넌트 언마운트 또는 월 변경 시 현재 월의 계산 상태만 정리
      calculatingMonthsRef.current.delete(yearMonth);
      // 🔧 FIX C14: 타이머 정리 (좀비 타이머 방지)
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (autoRefreshTimeoutRef.current) {
        clearTimeout(autoRefreshTimeoutRef.current);
        autoRefreshTimeoutRef.current = null;
      }
      retryCountRef.current = 0;
      logger.debug(`[useTeamPerformanceData] ${yearMonth} cleanup - 계산 상태 및 타이머 정리`);
    };
  }, [yearMonth]);

  // 🔧 개선된 자동 새로고침: 데이터 없을 때만 제한적으로 실행
  
  useEffect(() => {
    const autoRefreshOnNoData = async () => {
      // 🔧 조건: 로딩 완료 && 데이터 소스가 'none' && 현재 월이 계산 중이 아님 && 에러가 없음 && 이미 실행되지 않음 && 컨텍스트 로딩 완료
      if (!isLoading && 
          !isCalculating && 
          !contextLoading &&
          dataSource === 'none' && 
          !error && 
          !calculatingMonthsRef.current.has(yearMonth) &&
          !autoRefreshExecutedRef.current.has(yearMonth) &&
          teams.length > 0 &&
          employees.length > 0 &&
          budgetDataRef.current) {

        logger.debug(`[useTeamPerformanceData] ${yearMonth} 데이터 없음 감지 - 자동 새로고침 시작`);

        // 이 월에 대해 자동 새로고침 실행됨 표시
        autoRefreshExecutedRef.current.add(yearMonth);

        // 짧은 지연 후 자동 새로고침
        const delay = 300;
        autoRefreshTimeoutRef.current = setTimeout(() => {
          if (!calculatingMonthsRef.current.has(yearMonth) && !contextLoading) {
            logger.debug(`[useTeamPerformanceData] ${yearMonth} 자동 새로고침 실행`);
            refreshData();
          }
        }, delay);
      }
    };

    autoRefreshOnNoData();
  }, [isLoading, isCalculating, contextLoading, dataSource, error, yearMonth, refreshData, teams, employees]); // 🔧 FIX: budgetData 제거 (ref로 추적)


  return {
    performanceData,
    totalActualSales,
    totalWorkingDays,  // 🆕 전체 근무일
    isLoading,
    isCalculating,
    lastCalculatedAt,
    dataSource,
    refreshData,
    error
  };
}; 